import React, { useEffect, useState } from 'react';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { boxShadowValuesV2 } from '@shared/utils/style/dropShadows';
import { useSpring, animated } from 'react-spring';
import { cubicBezierEasingFn } from '@shared/utils/animationTransition';
import { useTranslation } from '@shared/core';

interface PromoCardBaseProps {
  /**
   * Space between the top of the card and the inner content to match your background image. Default is 200px.
   */
  innerTopHeight?: number;
  backgroundImageUrl?: string;
  title: string;
  subtitle: string;
  showDismissButton?: boolean;
  onPrimaryCtaClick?: () => void;
  onSecondaryCtaClick?: () => void;
  onDismissClick?: () => void;
  isVisible?: boolean;
}

interface PromoCardWithDefaultPrimaryCtaProps extends PromoCardBaseProps {
  primaryCtaLabel: string;
  primaryCtaLink?: string;
  renderPrimaryCta?: never;
  secondaryCtaLabel?: string;
  secondaryCtaLink?: string;
}

interface PromoCardWithDefaultSecondaryCtaProps extends PromoCardBaseProps {
  secondaryCtaLabel?: string;
  secondaryCtaLink?: string;
  renderSecondaryCta?: never;
  primaryCtaLabel?: string;
  primaryCtaLink?: string;
}

interface PromoCardWithCustomPrimaryCtaProps extends PromoCardBaseProps {
  primaryCtaLabel?: never;
  primaryCtaLink?: never;
  renderPrimaryCta: () => React.ReactNode;
  secondaryCtaLabel?: string;
  secondaryCtaLink?: string;
}

interface PromoCardWithCustomSecondaryCtaProps extends PromoCardBaseProps {
  primaryCtaLabel?: string;
  primaryCtaLink?: string;
  renderSecondaryCta: () => React.ReactNode;
  secondaryCtaLabel?: never;
  secondaryCtaLink?: never;
}

export type PromoCardProps =
  | PromoCardWithDefaultPrimaryCtaProps
  | PromoCardWithCustomPrimaryCtaProps
  | PromoCardWithDefaultSecondaryCtaProps
  | PromoCardWithCustomSecondaryCtaProps;

export const PromoCard = ({ innerTopHeight = 200, title, subtitle, showDismissButton, backgroundImageUrl, isVisible = true, ...ctaProps }: PromoCardProps) => {
  const [shouldRender, setShouldRender] = useState(isVisible);
  const { t2 } = useTranslation('adminDashboardV3');
  const { maybeLaterCta } = t2('dashboardAccordion', 'promoCards');

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
    }
  }, [isVisible]);

  const style = useSpring({
    display: 'grid',
    gridTemplateRows: isVisible ? '1fr' : '0fr',
    opacity: isVisible ? 1 : 0,
    config: {
      easing: cubicBezierEasingFn
    },
    onRest: () => {
      if (!isVisible) {
        setShouldRender(false);
      }
    }
  });

  if (!shouldRender) return null;

  return (
    <animated.div style={style}>
      <Flex
        width="100%"
        flexDirection={'column'}
        backgroundColor="white"
        overflow="hidden"
        borderRadius={pxToRem(12)}
        boxShadow={boxShadowValuesV2.xxxl}
        position={'relative'}
        rowGap={6}
      >
        <Box
          height={pxToRem(350)}
          position={'absolute'}
          top={0}
          width={'100%'}
          backgroundImage={backgroundImageUrl ? `url(${backgroundImageUrl})` : undefined}
          backgroundSize={'contain'}
          backgroundPosition={'top'}
          backgroundRepeat={'no-repeat'}
        />
        <Flex flexDirection={'column'} rowGap={3} paddingX={7} paddingTop={pxToRem(innerTopHeight)} zIndex={1}>
          <TextV2 typographyVariant="hed5" fontSize={26} textAlign="center">
            {title}
          </TextV2>
          <TextV2 typographyVariant="body2" fontSize={16} textAlign="center">
            {subtitle}
          </TextV2>
        </Flex>
        <Flex flexDirection={'column'} rowGap={5} paddingX={7} paddingBottom={6} zIndex={1}>
          {'primaryCtaLabel' in ctaProps && (
            <ButtonV2
              size="lg"
              variant="solid"
              shape="rounded"
              intent="neutral"
              onClick={'onPrimaryCtaClick' in ctaProps ? ctaProps.onPrimaryCtaClick : undefined}
              fullWidth
              {...(ctaProps.primaryCtaLink && { as: 'a', href: ctaProps.primaryCtaLink })}
            >
              {ctaProps.primaryCtaLabel}
            </ButtonV2>
          )}
          {'renderPrimaryCta' in ctaProps && ctaProps.renderPrimaryCta && ctaProps.renderPrimaryCta()}
          {'secondaryCtaLabel' in ctaProps && (
            <ButtonV2
              size="lg"
              variant="outline"
              shape="rounded"
              intent="neutral"
              onClick={'onSecondaryCtaClick' in ctaProps ? ctaProps.onSecondaryCtaClick : undefined}
              fullWidth
              {...(ctaProps.secondaryCtaLink && { as: 'a', href: ctaProps.secondaryCtaLink })}
            >
              {ctaProps.secondaryCtaLabel}
            </ButtonV2>
          )}
          {'renderSecondaryCta' in ctaProps && ctaProps.renderSecondaryCta && ctaProps.renderSecondaryCta()}
          {showDismissButton && (
            <ButtonV2 size="lg" variant="ghost" shape="rounded" intent="neutral" onClick={'onDismissClick' in ctaProps ? ctaProps.onDismissClick : undefined} fullWidth>
              {maybeLaterCta}
            </ButtonV2>
          )}
        </Flex>
      </Flex>
    </animated.div>
  );
};
